import { useCallback } from 'react';

import {
  CANNED_WIDGET_DEFAULT_FONT_SIZE_PT,
  CHECKBOX_CHECKED,
  CHECKBOX_OPTION_NODEID_SEPARATOR,
  computeCannedWidgetAutoFit,
  createAlineCanvas,
  GQL,
  PDF_CHECKBOX_UNCHECKED,
  PDFCustomDataKey,
  Queries,
} from '@aline/common';
import type { Core } from '@pdftron/webviewer';
import { pdfTronCore } from 'src/components/viewer/viewer-context';
import {
  CHECKBOX_LABEL_GAP_PX,
  CHECKBOX_LABEL_MAX_WIDTH_PX,
  defaultPDFFieldAttributes,
  getPageRotationDeg,
  getPdfStampDefaults,
  pdfCheckboxRowTopOffsetPx,
} from 'src/components/viewer/viewer-hooks';

import { useSyncAnnotations } from './annotations';

const PDF_CHECKBOX_LABEL_SUBJECT_PREFIX = 'AlineCbLbl:';
const CHECKBOX_LABEL_TOP_OFFSET_RATIO = 0.4;

/**
 * The strings of this enum MUST match what is in Apryse.  Check Apryse types for more info.
 */
export enum PDFFieldType {
  Btn = 'Btn',
  Sig = 'Sig',
  SigFormField = 'SignatureFormField',
  Text = 'Tx',
  TextFormField = 'TextFormField',
}

export function getPDFCheckboxSelectedAnswerIdsFromOptionFields(
  optionFields: Core.Annotations.Forms.Field[]
): UUID[] {
  return optionFields.flatMap(field => {
    if (String(field.getValue()) !== CHECKBOX_CHECKED) return [];
    const optionId = Queries.parseCheckboxOptionNodeId(field.name)?.optionId;
    return optionId ? [optionId] : [];
  });
}

export function getPDFCheckboxSelectedAnswerIds(
  fieldManager: Core.Annotations.Forms.FieldManager,
  variableNodeId: UUID,
  options: Array<{ id: string; optionText: string }>
): UUID[] {
  if (!options.length) return [];

  const optionFields = options
    .map(o => fieldManager.getField(Queries.getCheckboxOptionNodeId(variableNodeId, o.id)))
    .filter((field): field is Core.Annotations.Forms.Field => Boolean(field));

  return getPDFCheckboxSelectedAnswerIdsFromOptionFields(optionFields);
}

export interface PDFFieldLocation {
  pageNum: number;
  pageX: number;
  pageY: number;
}

export interface SyncPDFCheckboxWidgetsAfterOptionsEditOpts {
  nextOptions: Array<{ id: UUID; optionText?: string | null }>;
  selectedAnswerIds: UUID[];
  variableNodeId: UUID;
  previousOptions?: Array<{ id: UUID; optionText?: string | null }>;
}

interface CreatePDFSignatureFieldOpts {
  nodeId: UUID;
  location?: PDFFieldLocation;
  signatureVariant?: GQL.SignatureFieldVariant;
  value?: string | null;
}

interface CreatePDFCheckboxFieldOpts extends CreatePDFSignatureFieldOpts {
  checkboxOptions?: Array<{ id: UUID; optionText?: string | null }>;
  selectedAnswerIds?: UUID[];
}

interface CreatePDFNonSigFieldOpts extends CreatePDFSignatureFieldOpts {
  multiLine?: boolean;
  variant?: GQL.MetadataVariant;
}

interface UpdatePDFNonSigFieldMultilineProps {
  multiLine: boolean;
}

interface CreatePDFCannedFieldOpts {
  cannedFieldId: UUID;
  nodeId: UUID;
  location?: PDFFieldLocation;
}

// Canned widgets are always Times per `createPDFCannedField`; when that
// changes, plumb the family through the auto-fit inputs.
const CANNED_WIDGET_MEASURE_CSS_FAMILY = "'Times New Roman', Times, serif";

function applyCannedWidgetAutoFit(
  widget: Core.Annotations.WidgetAnnotation,
  field: Core.Annotations.Forms.Field,
  documentViewer: Core.DocumentViewer,
  value: string
): boolean {
  const { font } = field;
  if (!font) return false;

  const currentFontSizePt = font.size ?? CANNED_WIDGET_DEFAULT_FONT_SIZE_PT;
  // Match the compute fn's own 0 → default fallback so the caller measures at
  // the size the fn will actually reason about.
  const measureFontSizePt =
    currentFontSizePt > 0 ? currentFontSizePt : CANNED_WIDGET_DEFAULT_FONT_SIZE_PT;

  const canvas = createAlineCanvas();
  const context = canvas.getContext('2d');
  context.font = `${measureFontSizePt}px ${CANNED_WIDGET_MEASURE_CSS_FAMILY}`;
  const widthAtCurrentFontSizePt = value ? context.measureText(value).width : 0;

  const hasMultilineFlag = Boolean(field.flags?.get(pdfTronCore.Annotations.WidgetFlags.MULTILINE));
  const decision = computeCannedWidgetAutoFit({
    currentFontSizePt,
    hasMultilineFlag,
    pageWidthPt: documentViewer.getPageWidth(widget.getPageNumber()),
    widgetHeightPt: widget.Height,
    widgetWidthPt: widget.Width,
    widgetXPt: widget.X,
    widthAtCurrentFontSizePt,
  });
  if (!decision) return false;

  if (hasMultilineFlag) field.flags?.set(pdfTronCore.Annotations.WidgetFlags.MULTILINE, false);
  if (decision.fontSizePt !== currentFontSizePt) font.size = decision.fontSizePt;
  if (decision.widgetWidthPt !== widget.Width) {
    widget.setWidth(decision.widgetWidthPt);
    widget.setCustomData(PDFCustomDataKey.userSetAnnotWidth, String(decision.widgetWidthPt));
  }
  if (decision.widgetHeightPt !== widget.Height) {
    widget.setHeight(decision.widgetHeightPt);
    widget.setCustomData(PDFCustomDataKey.userSetAnnotHeight, String(decision.widgetHeightPt));
  }
  field.refreshAppearances();
  return true;
}

/**
 * Callers must pass the documentViewer. This hook is used from
 * both editor-scoped UI and app-level infrastructure, so it cannot safely fall
 * back to whichever runtime context happens to be current.
 */
export function usePDFFieldAPI(
  documentViewer: Core.DocumentViewer | null,
  tabId: UUID | null
): {
  createPDFCannedField: (options: CreatePDFCannedFieldOpts) => Promise<void>;
  createPDFCheckboxField: (options: CreatePDFCheckboxFieldOpts) => Promise<void>;
  createPDFNonSigField: (options: CreatePDFNonSigFieldOpts) => Promise<void>;
  createPDFSignatureField: (options: CreatePDFSignatureFieldOpts) => Promise<void>;
  deletePDFField: (nodeId: UUID, checkboxOptionIds?: UUID[]) => Promise<void>;
  getPDFFieldValue: (nodeId: UUID) => string | null;
  setPDFCannedFieldValue: (cannedFieldId: UUID, value: string) => void;
  setPDFCheckboxSelectionFromPanel: (options: {
    optionsList: Array<{ id: string; optionText: string }>;
    selectedAnswerIds: string[];
    variableNodeId: UUID;
  }) => Promise<void>;
  setPDFFieldValue: (nodeId: UUID, value: string) => void;
  syncPDFCheckboxWidgetsAfterOptionsEdit: (
    options: SyncPDFCheckboxWidgetsAfterOptionsEditOpts
  ) => Promise<void>;
  updatePDFTextFieldMultiline: (
    nodeId: UUID,
    props: UpdatePDFNonSigFieldMultilineProps
  ) => Promise<void>;
} {
  const { syncAnnotations } = useSyncAnnotations(tabId);
  const annotationManager = documentViewer?.getAnnotationManager() ?? null;
  const fieldManager = annotationManager?.getFieldManager() ?? null;
  const createPDFSignatureField = useCallback(
    async (options: CreatePDFSignatureFieldOpts): Promise<void> => {
      if (!documentViewer) return;

      const defaultLocation: PDFFieldLocation = {
        pageNum: documentViewer.getCurrentPage(),
        pageX: 100,
        pageY: 100,
      };
      const { location = defaultLocation, nodeId, signatureVariant } = options;
      const stampDefaults = getPdfStampDefaults(
        signatureVariant ?? GQL.SignatureFieldVariant.Signature
      );
      const flags = new pdfTronCore.Annotations.WidgetFlags({});
      flags.set(pdfTronCore.Annotations.WidgetFlags.REQUIRED, true);
      flags.set(pdfTronCore.Annotations.WidgetFlags.READ_ONLY, false);

      const annotationManager = documentViewer.getAnnotationManager();
      const field = new pdfTronCore.Annotations.Forms.Field(nodeId, {
        flags,
        type: PDFFieldType.Sig,
      });
      const widgetAnnot = new pdfTronCore.Annotations.SignatureWidgetAnnotation(field, {});

      widgetAnnot.setCustomData(PDFCustomDataKey.nodeId, nodeId);
      if (signatureVariant)
        widgetAnnot.setCustomData(PDFCustomDataKey.signatureVariant, signatureVariant);
      widgetAnnot.PageNumber = location.pageNum;
      widgetAnnot.X = location.pageX;
      widgetAnnot.Y = location.pageY;
      widgetAnnot.Width = stampDefaults.widthPx;
      widgetAnnot.Height = stampDefaults.heightPx;
      widgetAnnot.rotation = getPageRotationDeg(documentViewer, widgetAnnot.getPageNumber());

      annotationManager.getFieldManager().addField(field, true);
      annotationManager.addAnnotation(widgetAnnot);
      await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: location.pageNum });
      await syncAnnotations(documentViewer);
    },
    [documentViewer, syncAnnotations]
  );
  const createPDFNonSigField = useCallback(
    async (options: CreatePDFNonSigFieldOpts): Promise<void> => {
      if (!documentViewer) return;

      const defaultLocation: PDFFieldLocation = {
        pageNum: documentViewer.getCurrentPage(),
        pageX: 100,
        pageY: 100,
      };
      const { location = defaultLocation, multiLine = false, nodeId, value } = options;
      const flags = new pdfTronCore.Annotations.WidgetFlags({});
      // Set to READ_ONLY so only our inputs can programmatically change the field and validate field string values...
      flags.set(pdfTronCore.Annotations.WidgetFlags.READ_ONLY, true);
      flags.set(pdfTronCore.Annotations.WidgetFlags.MULTILINE, multiLine);

      const annotationManager = documentViewer.getAnnotationManager();
      const pdfDoc = await documentViewer.getDocument().getPDFDoc();
      const font = await pdfTronCore.PDFNet.Font.create(
        pdfDoc,
        pdfTronCore.PDFNet.Font.StandardType1Font.e_times_roman
      );
      const field = new pdfTronCore.Annotations.Forms.Field(nodeId, {
        flags,
        font: new pdfTronCore.Annotations.Font({
          name: await font.getName(),
          size: defaultPDFFieldAttributes.fontSizePx,
        }),
        type: PDFFieldType.Text,
        value: value ?? undefined,
      });

      const widgetAnnot = new pdfTronCore.Annotations.TextWidgetAnnotation(field, {});

      widgetAnnot.setCustomData(PDFCustomDataKey.nodeId, nodeId);
      widgetAnnot.PageNumber = location.pageNum;
      widgetAnnot.X = location.pageX;
      widgetAnnot.Y = location.pageY;
      widgetAnnot.Width = defaultPDFFieldAttributes.nonSigFieldWidthPx;
      widgetAnnot.Height = defaultPDFFieldAttributes.nonSigFieldHeightPx;
      widgetAnnot.rotation = getPageRotationDeg(documentViewer, widgetAnnot.getPageNumber());

      annotationManager.getFieldManager().addField(field, true);
      annotationManager.addAnnotation(widgetAnnot);
      await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: location.pageNum });
      await syncAnnotations(documentViewer);
    },
    [documentViewer, syncAnnotations]
  );
  const createPDFCannedField = useCallback(
    async (options: CreatePDFCannedFieldOpts): Promise<void> => {
      if (!documentViewer) return;

      const defaultLocation: PDFFieldLocation = {
        pageNum: documentViewer.getCurrentPage(),
        pageX: 100,
        pageY: 100,
      };
      const { cannedFieldId, location = defaultLocation, nodeId } = options;

      /**
       * Widget is READ_ONLY: recipients edit canned values via the drawer
       * (desktop) or bottom sheet (mobile), never by typing into the PDF
       * widget itself. `useSyncCannedFieldValuesToPdf` reflects the DB value
       * onto the widget on load and after each update.
       */
      const flags = new pdfTronCore.Annotations.WidgetFlags({});
      flags.set(pdfTronCore.Annotations.WidgetFlags.READ_ONLY, true);

      const annotationManager = documentViewer.getAnnotationManager();
      const pdfDoc = await documentViewer.getDocument().getPDFDoc();
      const font = await pdfTronCore.PDFNet.Font.create(
        pdfDoc,
        pdfTronCore.PDFNet.Font.StandardType1Font.e_times_roman
      );

      const field = new pdfTronCore.Annotations.Forms.Field(nodeId, {
        flags,
        font: new pdfTronCore.Annotations.Font({
          name: await font.getName(),
          size: defaultPDFFieldAttributes.fontSizePx,
        }),
        type: PDFFieldType.Text,
      });
      const widgetAnnot = new pdfTronCore.Annotations.TextWidgetAnnotation(field, {});

      widgetAnnot.setCustomData(PDFCustomDataKey.nodeId, nodeId);
      widgetAnnot.setCustomData(PDFCustomDataKey.cannedFieldId, cannedFieldId);
      widgetAnnot.PageNumber = location.pageNum;
      widgetAnnot.X = location.pageX;
      widgetAnnot.Y = location.pageY;
      widgetAnnot.Width = defaultPDFFieldAttributes.nonSigFieldWidthPx;
      widgetAnnot.Height = defaultPDFFieldAttributes.nonSigFieldHeightPx;
      widgetAnnot.rotation = getPageRotationDeg(documentViewer, widgetAnnot.getPageNumber());

      annotationManager.getFieldManager().addField(field, true);
      annotationManager.addAnnotation(widgetAnnot);
      await annotationManager.drawAnnotations({ majorRedraw: true, pageNumber: location.pageNum });
      await syncAnnotations(documentViewer);
    },
    [documentViewer, syncAnnotations]
  );
  const setPDFCannedFieldValue = useCallback(
    (cannedFieldId: UUID, value: string): void => {
      if (!annotationManager) return;

      const widgetRefs = annotationManager
        .getAnnotationsList()
        .filter(
          (annot): annot is Core.Annotations.WidgetAnnotation =>
            annot instanceof pdfTronCore.Annotations.WidgetAnnotation
        )
        .map(widget => ({
          cannedFieldId: widget.getCustomData(PDFCustomDataKey.cannedFieldId),
          pageNumber: widget.PageNumber,
          widget,
        }));

      const { widgets } = selectCannedFieldWidgets(widgetRefs, cannedFieldId);
      if (!widgets.length) return;

      const changedPageNumbers = new Set<number>();
      for (const { pageNumber, widget } of widgets) {
        /**
         * Each placement owns its own field (named by that placement's nodeId),
         * so the value has to be written once per widget.
         */
        const field = widget.getField();
        if (String(field.getValue() ?? '') !== value) {
          field.setValue(value);
          changedPageNumbers.add(pageNumber);
        }
        const inner = widget.innerElement;
        if (inner instanceof HTMLInputElement || inner instanceof HTMLTextAreaElement)
          inner.value = value;
        if (documentViewer && applyCannedWidgetAutoFit(widget, field, documentViewer, value))
          changedPageNumbers.add(pageNumber);
      }

      if (changedPageNumbers.size > 0) {
        void Promise.all(
          [...changedPageNumbers].map(pageNumber =>
            annotationManager.drawAnnotations({ majorRedraw: true, pageNumber })
          )
        );
      }
    },
    [annotationManager, documentViewer]
  );
  const deletePDFField = useCallback(
    async (nodeId: UUID, checkboxOptionIds?: UUID[]): Promise<void> => {
      if (!documentViewer || !annotationManager) return;
      const fieldManager = annotationManager.getFieldManager();
      const fromArgs = checkboxOptionIds?.length
        ? [...checkboxOptionIds.map(oid => Queries.getCheckboxOptionNodeId(nodeId, oid)), nodeId]
        : [nodeId];
      const discovered = collectCheckboxRelatedFieldNodeIdsFromAnnotations(
        annotationManager,
        nodeId
      );
      const nodeIdsToDelete = [...new Set([...fromArgs, ...discovered])];
      const labelsToDelete = collectCheckboxLabelAnnotations(annotationManager, nodeId);
      const widgetsToDelete: Parameters<typeof annotationManager.deleteAnnotations>[0] = [];
      for (const nid of nodeIdsToDelete) {
        const fieldOrWidgetAnnot = fieldManager.getField(nid);
        if (!fieldOrWidgetAnnot) continue;
        const widgets =
          fieldOrWidgetAnnot instanceof pdfTronCore.Annotations.WidgetAnnotation
            ? [fieldOrWidgetAnnot]
            : (fieldOrWidgetAnnot.widgets ?? []);
        widgetsToDelete.push(...widgets);
      }
      const byId = new Map<string, Core.Annotations.Annotation>();
      for (const annotation of [...labelsToDelete, ...widgetsToDelete]) {
        byId.set(annotation.Id, annotation);
      }
      const annotationsToRemove = [...byId.values()];
      if (annotationsToRemove.length === 0) return;

      annotationManager.ungroupAnnotations(annotationsToRemove);
      annotationManager.deleteAnnotations(annotationsToRemove);
      await syncAnnotations(documentViewer);
    },
    [annotationManager, documentViewer, syncAnnotations]
  );
  const getPDFFieldValue = useCallback(
    (nodeId: UUID): string | null => {
      if (!fieldManager) return null;

      const fieldOrWidgetAnnot = fieldManager.getField(nodeId);
      if (!fieldOrWidgetAnnot) return null;

      if (fieldOrWidgetAnnot instanceof pdfTronCore.Annotations.WidgetAnnotation) {
        const widgetField = fieldOrWidgetAnnot.getField();
        return String(widgetField.getValue());
      }

      return String(fieldOrWidgetAnnot.getValue());
    },
    [fieldManager]
  );
  const setPDFFieldValue = useCallback(
    (nodeId: UUID, value: string): void => {
      if (!fieldManager || !annotationManager) return;

      const fieldOrWidgetAnnot = fieldManager.getField(nodeId);
      // Despite types, this can be null...
      if (!fieldOrWidgetAnnot) return;

      const field =
        fieldOrWidgetAnnot instanceof pdfTronCore.Annotations.WidgetAnnotation
          ? fieldOrWidgetAnnot.getField()
          : fieldOrWidgetAnnot;

      field.setValue(value);

      const pagesToRedraw = new Set<number>();
      for (const widget of field.widgets ?? []) {
        if (!(widget instanceof pdfTronCore.Annotations.WidgetAnnotation)) continue;
        // Skip only when the widget carries a nodeId that belongs to a
        // different field. Template-launched contracts open with widgets that
        // have no nodeId custom data yet — dropping them here left the DOM
        // input empty on the fresh copy.
        const widgetNodeId = widget.getCustomData(PDFCustomDataKey.nodeId);
        if (widgetNodeId && widgetNodeId !== nodeId) continue;

        const inner = widget.innerElement;
        if (inner instanceof HTMLInputElement || inner instanceof HTMLTextAreaElement) {
          inner.value = value;
        }
        if (widget.PageNumber) pagesToRedraw.add(widget.PageNumber);
      }

      if (pagesToRedraw.size > 0) {
        void Promise.all(
          [...pagesToRedraw].map(pageNumber =>
            annotationManager.drawAnnotations({ majorRedraw: true, pageNumber })
          )
        );
      }
    },
    [annotationManager, fieldManager]
  );
  const setPDFCheckboxSelectionFromPanel = useCallback(
    async (options: {
      optionsList: Array<{ id: string; optionText: string }>;
      selectedAnswerIds: string[];
      variableNodeId: UUID;
    }): Promise<void> => {
      if (!documentViewer || !annotationManager || !fieldManager) return;

      const { optionsList, selectedAnswerIds, variableNodeId } = options;
      if (optionsList.length === 0) return;

      const pagesToRedraw = new Set<number>();

      const lockCheckboxWidgetSquare = (widget: Core.Annotations.WidgetAnnotation | null): void => {
        if (!widget) return;
        widget.Width = defaultPDFFieldAttributes.fontSizePx;
        widget.Height = defaultPDFFieldAttributes.fontSizePx;
      };

      let hasAnyOptionWidget = false;
      for (const option of optionsList) {
        const nodeId = Queries.getCheckboxOptionNodeId(variableNodeId, option.id);
        const fieldOrWidget = fieldManager.getField(nodeId);
        if (!fieldOrWidget) continue;
        hasAnyOptionWidget = true;
        fieldOrWidget.setValue(
          selectedAnswerIds.includes(option.id) ? CHECKBOX_CHECKED : PDF_CHECKBOX_UNCHECKED
        );
        const widgetAnnotation = getCheckboxWidgetForOptionField(fieldManager, nodeId);
        lockCheckboxWidgetSquare(widgetAnnotation);
        if (widgetAnnotation?.PageNumber) pagesToRedraw.add(widgetAnnotation.PageNumber);
      }

      if (!hasAnyOptionWidget) return;

      await Promise.all(
        [...pagesToRedraw].map(pageNumber =>
          annotationManager.drawAnnotations({ majorRedraw: true, pageNumber })
        )
      );
      await syncAnnotations(documentViewer);
    },
    [annotationManager, documentViewer, fieldManager, syncAnnotations]
  );
  const updatePDFTextFieldMultiline = useCallback(
    async (nodeId: UUID, props: UpdatePDFNonSigFieldMultilineProps): Promise<void> => {
      if (!documentViewer || !annotationManager || !fieldManager) return;

      const { multiLine } = props;
      // Despite types, this can be null...
      const field = fieldManager.getField(nodeId);
      const isField = field instanceof pdfTronCore.Annotations.Forms.Field;
      if (!isField) return;

      const widgets = field.widgets ?? [];
      field.flags?.set(pdfTronCore.Annotations.WidgetFlags.MULTILINE, multiLine);

      await Promise.all(
        widgets.map(async widget => {
          // We have to recreate the widget otherwise the input doesn't switch to a textarea if multiline and back to input if single line.
          const replacementWidget = new pdfTronCore.Annotations.TextWidgetAnnotation(field, {});

          Object.values(PDFCustomDataKey).forEach(dataKey => {
            if (dataKey === PDFCustomDataKey.signatureImgId) return;

            const originalVal = widget.getCustomData(dataKey);
            replacementWidget.setCustomData(dataKey, originalVal);
          });
          replacementWidget.PageNumber = widget.PageNumber;
          replacementWidget.X = widget.X;
          replacementWidget.Y = widget.Y;
          replacementWidget.Width = widget.Width;
          replacementWidget.rotation = getPageRotationDeg(
            documentViewer,
            replacementWidget.getPageNumber()
          );
          // If toggling back to single-line, adjust height back to single line...
          replacementWidget.Height = !multiLine
            ? defaultPDFFieldAttributes.nonSigFieldHeightPx
            : widget.Height;

          annotationManager.deleteAnnotation(widget, { force: true });
          annotationManager.addAnnotation(replacementWidget);
          await annotationManager.drawAnnotations({
            majorRedraw: true,
            pageNumber: replacementWidget.PageNumber,
          });
        })
      );

      await syncAnnotations(documentViewer);
    },
    [annotationManager, documentViewer, fieldManager, syncAnnotations]
  );
  const createPDFCheckboxField = useCallback(
    async (options: CreatePDFCheckboxFieldOpts): Promise<void> => {
      if (!documentViewer) return;
      const { checkboxOptions } = options;
      if (!checkboxOptions?.length) return;

      const defaultLocation: PDFFieldLocation = {
        pageNum: documentViewer.getCurrentPage(),
        pageX: 100,
        pageY: 100,
      };
      const {
        location = defaultLocation,
        nodeId: variableNodeId,
        selectedAnswerIds = [],
      } = options;
      const annotationManager = documentViewer.getAnnotationManager();
      const heightPx = defaultPDFFieldAttributes.nonSigFieldHeightPx;

      const fieldManager = annotationManager.getFieldManager();
      const widgetsToDraw: Core.Annotations.Annotation[] = [];
      const rotation = getPageRotationDeg(documentViewer, location.pageNum);
      for (let i = 0; i < checkboxOptions.length; i++) {
        addMultiCheckboxOptionRowAtIndex({
          anchorRowTopY: location.pageY,
          annotationManager,
          fieldManager,
          heightPx,
          option: checkboxOptions[i],
          pageNum: location.pageNum,
          pageX: location.pageX,
          rotation,
          rowIndex: i,
          selectedAnswerIds,
          variableNodeId,
          widgetsToDraw,
        });
      }
      await annotationManager.drawAnnotationsFromList(widgetsToDraw);
      await syncAnnotations(documentViewer);
    },
    [documentViewer, syncAnnotations]
  );

  const syncPDFCheckboxWidgetsAfterOptionsEdit = useCallback(
    async (options: SyncPDFCheckboxWidgetsAfterOptionsEditOpts): Promise<void> => {
      if (!documentViewer || !annotationManager || !fieldManager) return;

      const { nextOptions, previousOptions, selectedAnswerIds, variableNodeId } = options;
      const previousRows = previousOptions ?? [];
      const previousOptionIds = previousRows.map(option => option.id);
      const diff = computeCheckboxOptionsDiff(previousRows, nextOptions);
      const candidateOptionIds = [
        ...new Set([...previousOptionIds, ...nextOptions.map(option => option.id)]),
      ];
      const defaultLocation: PDFFieldLocation = {
        pageNum: documentViewer.getCurrentPage(),
        pageX: 100,
        pageY: 100,
      };

      if (nextOptions.length === 0) {
        await deletePDFField(
          variableNodeId,
          previousOptionIds.length > 0 ? previousOptionIds : undefined
        );
        return;
      }

      const hasAnyExistingWidget =
        collectCheckboxRelatedFieldNodeIdsFromAnnotations(annotationManager, variableNodeId)
          .length > 0;

      if (!hasAnyExistingWidget) {
        await createPDFCheckboxField({
          checkboxOptions: nextOptions,
          location: checkboxPdfLocation(
            findFirstCheckboxPdfWidget(
              fieldManager,
              annotationManager,
              variableNodeId,
              candidateOptionIds
            ),
            defaultLocation
          ),
          nodeId: variableNodeId,
          selectedAnswerIds,
        });
        return;
      }

      const layoutAnchor =
        findCheckboxGroupLayoutAnchor(
          fieldManager,
          annotationManager,
          variableNodeId,
          nextOptions
        ) ??
        findCheckboxGroupLayoutAnchor(
          fieldManager,
          annotationManager,
          variableNodeId,
          previousRows
        );

      if (!layoutAnchor) {
        const location = checkboxPdfLocation(
          findFirstCheckboxPdfWidget(
            fieldManager,
            annotationManager,
            variableNodeId,
            candidateOptionIds
          ),
          defaultLocation
        );
        await deletePDFField(
          variableNodeId,
          previousOptionIds.length > 0 ? previousOptionIds : undefined
        );
        await createPDFCheckboxField({
          checkboxOptions: nextOptions,
          location,
          nodeId: variableNodeId,
          selectedAnswerIds,
        });
        return;
      }

      const heightPx = defaultPDFFieldAttributes.nonSigFieldHeightPx;
      const baseAnchorRowTopY = layoutAnchor.anchorRowTopY - layoutAnchor.anchorRowIndex * heightPx;
      const structureChanged =
        diff.removedIds.length > 0 || diff.addedOptions.length > 0 || diff.orderChanged;

      const orphanedOptionIds = getOrphanedCheckboxOptionIds(
        annotationManager,
        variableNodeId,
        nextOptions
      );
      const optionIdsToDelete = [...new Set([...diff.removedIds, ...orphanedOptionIds])];
      if (optionIdsToDelete.length > 0) {
        await deletePDFCheckboxOptionRowsOnly({
          annotationManager,
          documentViewer,
          optionIds: optionIdsToDelete,
          syncAnnotations,
          variableNodeId,
        });
      }

      const labelRedraw: Core.Annotations.Annotation[] = [];
      for (const option of nextOptions) {
        const optionNodeId = Queries.getCheckboxOptionNodeId(variableNodeId, option.id);
        const widgetAnnotation = getCheckboxWidgetForOption(
          fieldManager,
          annotationManager,
          variableNodeId,
          option.id,
          nextOptions
        );
        if (!widgetAnnotation?.PageNumber) continue;

        const previousText = diff.previousTextById.get(option.id) ?? '';
        const nextText = (option.optionText ?? '').trim();
        const labelAnnotation = findFreeTextLabelForCheckboxOptionKey(
          annotationManager,
          optionNodeId
        );

        if (labelAnnotation && !nextText) {
          annotationManager.ungroupAnnotations([labelAnnotation]);
          annotationManager.deleteAnnotations([labelAnnotation]);
          continue;
        }
        if (labelAnnotation) {
          if (previousText !== nextText) {
            labelAnnotation.setContents(nextText || ' ');
            labelRedraw.push(labelAnnotation);
          }
          continue;
        }
        if (!nextText) continue;

        labelRedraw.push(
          createCheckboxOptionLabelForWidget({
            annotationManager,
            labelText: nextText,
            optionNodeId,
            pageNum: widgetAnnotation.PageNumber,
            pageX: widgetAnnotation.X,
            rotation: widgetAnnotation.rotation ?? 0,
            rowTopY: anchorRowTopYFromCheckboxWidget(widgetAnnotation),
            widgetAnnotation,
          })
        );
      }

      const widgetsToDraw: Core.Annotations.Annotation[] = [];
      const addedOptionsNeedingWidgets = diff.addedOptions.filter(
        option =>
          !getCheckboxWidgetForOption(
            fieldManager,
            annotationManager,
            variableNodeId,
            option.id,
            nextOptions
          )
      );
      if (addedOptionsNeedingWidgets.length > 0) {
        const rotation = getPageRotationDeg(documentViewer, layoutAnchor.pageNum);
        for (const option of addedOptionsNeedingWidgets) {
          const rowIndex = nextOptions.findIndex(nextOption => nextOption.id === option.id);
          if (rowIndex < 0) continue;
          addMultiCheckboxOptionRowAtIndex({
            anchorRowTopY: baseAnchorRowTopY,
            annotationManager,
            fieldManager,
            heightPx,
            option,
            pageNum: layoutAnchor.pageNum,
            pageX: layoutAnchor.pageX,
            rotation,
            rowIndex,
            selectedAnswerIds,
            variableNodeId,
            widgetsToDraw,
          });
        }
      }

      if (labelRedraw.length > 0) {
        await annotationManager.drawAnnotationsFromList(labelRedraw);
      }
      if (widgetsToDraw.length > 0) {
        await annotationManager.drawAnnotationsFromList(widgetsToDraw);
      }

      if (structureChanged) {
        await repositionMultiCheckboxOptionRows({
          annotationManager,
          baseAnchorRowTopY,
          documentViewer,
          fieldManager,
          layoutAnchor,
          nextOptions,
          syncAnnotations,
          variableNodeId,
        });
        return;
      }

      await syncAnnotations(documentViewer);
    },
    [
      annotationManager,
      createPDFCheckboxField,
      deletePDFField,
      documentViewer,
      fieldManager,
      syncAnnotations,
    ]
  );

  return {
    createPDFCannedField,
    createPDFCheckboxField,
    createPDFNonSigField,
    createPDFSignatureField,
    deletePDFField,
    getPDFFieldValue,
    setPDFCannedFieldValue,
    setPDFCheckboxSelectionFromPanel,
    setPDFFieldValue,
    syncPDFCheckboxWidgetsAfterOptionsEdit,
    updatePDFTextFieldMultiline,
  };
}

interface CheckboxGroupLayoutAnchor {
  anchorRowIndex: number;
  anchorRowTopY: number;
  pageNum: number;
  pageX: number;
}

function findFirstCheckboxPdfWidget(
  fieldManager: Core.Annotations.Forms.FieldManager,
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID,
  optionIds: UUID[]
): Core.Annotations.WidgetAnnotation | null {
  const fromVariable = getCheckboxWidgetByNodeId(fieldManager, annotationManager, variableNodeId);
  if (fromVariable) return fromVariable;
  for (const optionId of optionIds) {
    const widget = getCheckboxWidgetByNodeId(
      fieldManager,
      annotationManager,
      Queries.getCheckboxOptionNodeId(variableNodeId, optionId)
    );
    if (widget) return widget;
  }
  return null;
}

function findCheckboxGroupLayoutAnchor(
  fieldManager: Core.Annotations.Forms.FieldManager,
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID,
  options: Array<{ id: UUID }>
): CheckboxGroupLayoutAnchor | null {
  for (let anchorRowIndex = 0; anchorRowIndex < options.length; anchorRowIndex++) {
    const widget = getCheckboxWidgetForOption(
      fieldManager,
      annotationManager,
      variableNodeId,
      options[anchorRowIndex].id,
      options
    );
    if (!widget?.PageNumber) continue;
    return {
      anchorRowIndex,
      anchorRowTopY: anchorRowTopYFromCheckboxWidget(widget),
      pageNum: widget.PageNumber,
      pageX: widget.X,
    };
  }
  return null;
}

function checkboxPdfLocation(
  widget: Core.Annotations.WidgetAnnotation | null,
  fallback: PDFFieldLocation
): PDFFieldLocation {
  if (!widget?.PageNumber) return fallback;
  return {
    pageNum: widget.PageNumber,
    pageX: widget.X,
    pageY: anchorRowTopYFromCheckboxWidget(widget),
  };
}

function createCheckboxOptionLabelForWidget(options: {
  annotationManager: Core.AnnotationManager;
  labelText: string;
  optionNodeId: string;
  pageNum: number;
  pageX: number;
  rotation: number;
  rowTopY: number;
  widgetAnnotation: Core.Annotations.WidgetAnnotation;
}): InstanceType<typeof pdfTronCore.Annotations.FreeTextAnnotation> {
  const {
    annotationManager,
    labelText,
    optionNodeId,
    pageNum,
    pageX,
    rotation,
    rowTopY,
    widgetAnnotation,
  } = options;
  const labelAnnotation = new pdfTronCore.Annotations.FreeTextAnnotation(
    pdfTronCore.Annotations.FreeTextAnnotation.Intent.FreeText
  );
  labelAnnotation.setContents(labelText);
  labelAnnotation.PageNumber = pageNum;
  labelAnnotation.X = pageX + defaultPDFFieldAttributes.fontSizePx + CHECKBOX_LABEL_GAP_PX;
  labelAnnotation.Y =
    rowTopY + defaultPDFFieldAttributes.fontSizePx * CHECKBOX_LABEL_TOP_OFFSET_RATIO;
  labelAnnotation.Width = CHECKBOX_LABEL_MAX_WIDTH_PX;
  labelAnnotation.Height = defaultPDFFieldAttributes.nonSigFieldHeightPx;
  labelAnnotation.Rotation = rotation;
  labelAnnotation.FillColor = new pdfTronCore.Annotations.Color(255, 255, 255, 0);
  labelAnnotation.StrokeColor = new pdfTronCore.Annotations.Color(255, 255, 255, 0);
  labelAnnotation.StrokeThickness = 0;
  labelAnnotation.ReadOnly = false;
  labelAnnotation.LockedContents = true;
  labelAnnotation.FontSize = `${defaultPDFFieldAttributes.fontSizePx}pt`;
  applyCheckboxOptionLabelFreeTextStyle(labelAnnotation);
  labelAnnotation.Subject = `${PDF_CHECKBOX_LABEL_SUBJECT_PREFIX}${optionNodeId}`;
  labelAnnotation.setCustomData(PDFCustomDataKey.checkboxOptionLabel, optionNodeId);
  annotationManager.addAnnotation(labelAnnotation);
  annotationManager.groupAnnotations(widgetAnnotation, [labelAnnotation]);
  return labelAnnotation;
}

function getCheckboxOptionLabelKeyFromFreeText(
  annotation: Core.Annotations.Annotation
): string | null {
  if (!(annotation instanceof pdfTronCore.Annotations.FreeTextAnnotation)) return null;
  const subject = annotation.Subject;
  if (typeof subject === 'string' && subject.startsWith(PDF_CHECKBOX_LABEL_SUBJECT_PREFIX)) {
    return subject.slice(PDF_CHECKBOX_LABEL_SUBJECT_PREFIX.length);
  }
  const tag = annotation.getCustomData(PDFCustomDataKey.checkboxOptionLabel);
  return tag ? String(tag) : null;
}

function collectCheckboxLabelAnnotations(
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID
): Core.Annotations.Annotation[] {
  const optPrefix = `${variableNodeId}${CHECKBOX_OPTION_NODEID_SEPARATOR}`;
  const list = annotationManager.getAnnotationsList();
  const out: Core.Annotations.Annotation[] = [];
  for (const annotation of list) {
    const key = getCheckboxOptionLabelKeyFromFreeText(annotation);
    if (!key) continue;
    if (key === variableNodeId || key.startsWith(optPrefix)) out.push(annotation);
  }
  return out;
}

function collectCheckboxRelatedFieldNodeIdsFromAnnotations(
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID
): UUID[] {
  const prefix = `${variableNodeId}${CHECKBOX_OPTION_NODEID_SEPARATOR}`;
  const seen = new Set<string>();
  for (const annotation of annotationManager.getAnnotationsList()) {
    const nodeId = annotation.getCustomData(PDFCustomDataKey.nodeId);
    if (!nodeId || typeof nodeId !== 'string') continue;
    if (nodeId === variableNodeId || nodeId.startsWith(prefix)) seen.add(nodeId);
  }
  return [...seen] as UUID[];
}

function applyCheckboxOptionLabelFreeTextStyle(
  labelAnnotation: InstanceType<typeof pdfTronCore.Annotations.FreeTextAnnotation>
): void {
  labelAnnotation.TextColor = new pdfTronCore.Annotations.Color(0, 0, 0, 1);
  labelAnnotation.TextAlign = 'left';
  labelAnnotation.TextVerticalAlign = 'top';
  labelAnnotation.setAutoSizeType(pdfTronCore.Annotations.FreeTextAnnotation.AutoSizeTypes.NONE);
}

function collectCheckboxLabelAnnotationsForOptionKeys(
  annotationManager: Core.AnnotationManager,
  optionKeys: Iterable<string>
): Core.Annotations.Annotation[] {
  const keySet = new Set(optionKeys);
  const list = annotationManager.getAnnotationsList();
  return list.filter(annotation => {
    const key = getCheckboxOptionLabelKeyFromFreeText(annotation);
    return key !== null && keySet.has(key);
  });
}

function getOrphanedCheckboxOptionIds(
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID,
  nextOptions: Array<{ id: UUID }>
): UUID[] {
  const nextOptionNodeIds = new Set(
    nextOptions.map(option => Queries.getCheckboxOptionNodeId(variableNodeId, option.id))
  );
  return collectCheckboxRelatedFieldNodeIdsFromAnnotations(
    annotationManager,
    variableNodeId
  ).flatMap(nodeId => {
    if (nodeId === variableNodeId || nextOptionNodeIds.has(nodeId)) return [];
    const parsed = Queries.parseCheckboxOptionNodeId(nodeId);
    return parsed ? [parsed.optionId] : [];
  });
}

function getCheckboxWidgetForOption(
  fieldManager: Core.Annotations.Forms.FieldManager,
  annotationManager: Core.AnnotationManager,
  variableNodeId: UUID,
  optionId: UUID,
  nextOptions?: Array<{ id: UUID }>
): Core.Annotations.WidgetAnnotation | null {
  const optionNodeId = Queries.getCheckboxOptionNodeId(variableNodeId, optionId);
  const widget = getCheckboxWidgetByNodeId(fieldManager, annotationManager, optionNodeId);
  if (widget) return widget;
  if (nextOptions?.[0]?.id !== optionId) return null;
  return getCheckboxWidgetByNodeId(fieldManager, annotationManager, variableNodeId);
}

function getCheckboxWidgetByNodeId(
  fieldManager: Core.Annotations.Forms.FieldManager,
  annotationManager: Core.AnnotationManager,
  nodeId: string
): Core.Annotations.WidgetAnnotation | null {
  const fieldOrWidget = fieldManager.getField(nodeId);
  if (fieldOrWidget) {
    return fieldOrWidget instanceof pdfTronCore.Annotations.WidgetAnnotation
      ? fieldOrWidget
      : (fieldOrWidget.widgets?.[0] ?? null);
  }
  for (const annotation of annotationManager.getAnnotationsList()) {
    if (!(annotation instanceof pdfTronCore.Annotations.CheckButtonWidgetAnnotation)) continue;
    if (annotation.getCustomData(PDFCustomDataKey.nodeId) === nodeId) return annotation;
  }
  return null;
}

function getCheckboxWidgetForOptionField(
  fieldManager: Core.Annotations.Forms.FieldManager,
  optionNodeId: string
): Core.Annotations.WidgetAnnotation | null {
  const fieldOrWidget = fieldManager.getField(optionNodeId);
  if (!fieldOrWidget) return null;
  return fieldOrWidget instanceof pdfTronCore.Annotations.WidgetAnnotation
    ? fieldOrWidget
    : (fieldOrWidget.widgets?.[0] ?? null);
}

function findFreeTextLabelForCheckboxOptionKey(
  annotationManager: Core.AnnotationManager,
  optionOrVariableKey: string
): InstanceType<typeof pdfTronCore.Annotations.FreeTextAnnotation> | null {
  for (const annotation of annotationManager.getAnnotationsList()) {
    if (!(annotation instanceof pdfTronCore.Annotations.FreeTextAnnotation)) continue;
    if (getCheckboxOptionLabelKeyFromFreeText(annotation) === optionOrVariableKey) {
      return annotation;
    }
  }
  return null;
}

function anchorRowTopYFromCheckboxWidget(widget: Core.Annotations.WidgetAnnotation): number {
  return widget.Y - pdfCheckboxRowTopOffsetPx;
}

async function deletePDFCheckboxOptionRowsOnly(options: {
  annotationManager: Core.AnnotationManager;
  documentViewer: Core.DocumentViewer;
  optionIds: UUID[];
  syncAnnotations: (documentViewer: Core.DocumentViewer) => Promise<unknown>;
  variableNodeId: UUID;
}): Promise<void> {
  const { annotationManager, documentViewer, optionIds, syncAnnotations, variableNodeId } = options;
  if (optionIds.length === 0) return;
  const fieldManager = annotationManager.getFieldManager();
  const optionNodeIds = optionIds.map(optionId =>
    Queries.getCheckboxOptionNodeId(variableNodeId, optionId)
  );
  const labels = collectCheckboxLabelAnnotationsForOptionKeys(annotationManager, optionNodeIds);
  const widgets: Core.Annotations.Annotation[] = [];
  for (const nodeId of optionNodeIds) {
    const widgetAnnotation = fieldManager.getField(nodeId);
    if (!widgetAnnotation) continue;
    const widgetAnnotations =
      widgetAnnotation instanceof pdfTronCore.Annotations.WidgetAnnotation
        ? [widgetAnnotation]
        : (widgetAnnotation.widgets ?? []);
    widgets.push(...widgetAnnotations);
  }
  const byId = new Map<string, Core.Annotations.Annotation>();
  for (const annotation of [...labels, ...widgets]) {
    byId.set(annotation.Id, annotation);
  }
  const toRemove = [...byId.values()];
  if (toRemove.length === 0) return;
  annotationManager.ungroupAnnotations(toRemove);
  annotationManager.deleteAnnotations(toRemove);
  await syncAnnotations(documentViewer);
}

async function repositionMultiCheckboxOptionRows(options: {
  annotationManager: Core.AnnotationManager;
  baseAnchorRowTopY: number;
  documentViewer: Core.DocumentViewer;
  fieldManager: Core.Annotations.Forms.FieldManager;
  layoutAnchor: CheckboxGroupLayoutAnchor;
  nextOptions: Array<{ id: UUID; optionText?: string | null }>;
  syncAnnotations: (documentViewer: Core.DocumentViewer) => Promise<unknown>;
  variableNodeId: UUID;
}): Promise<void> {
  const {
    annotationManager,
    baseAnchorRowTopY,
    documentViewer,
    fieldManager,
    layoutAnchor,
    nextOptions,
    syncAnnotations,
    variableNodeId,
  } = options;
  if (nextOptions.length === 0) return;

  const heightPx = defaultPDFFieldAttributes.nonSigFieldHeightPx;
  const rotation = getPageRotationDeg(documentViewer, layoutAnchor.pageNum);
  const toDraw: Core.Annotations.Annotation[] = [];

  for (let i = 0; i < nextOptions.length; i++) {
    const widgetAnnotation = getCheckboxWidgetForOption(
      fieldManager,
      annotationManager,
      variableNodeId,
      nextOptions[i].id,
      nextOptions
    );
    if (!widgetAnnotation) continue;
    const nodeId = Queries.getCheckboxOptionNodeId(variableNodeId, nextOptions[i].id);
    const rowTopY = baseAnchorRowTopY + i * heightPx;
    const checkboxY = rowTopY + (heightPx - defaultPDFFieldAttributes.fontSizePx) / 2;
    widgetAnnotation.X = layoutAnchor.pageX;
    widgetAnnotation.Y = checkboxY;
    widgetAnnotation.PageNumber = layoutAnchor.pageNum;
    widgetAnnotation.rotation = rotation;
    toDraw.push(widgetAnnotation);
    const labelAnnotation = findFreeTextLabelForCheckboxOptionKey(annotationManager, nodeId);
    if (labelAnnotation) {
      labelAnnotation.X =
        layoutAnchor.pageX + defaultPDFFieldAttributes.fontSizePx + CHECKBOX_LABEL_GAP_PX;
      labelAnnotation.Y =
        rowTopY + defaultPDFFieldAttributes.fontSizePx * CHECKBOX_LABEL_TOP_OFFSET_RATIO;
      labelAnnotation.PageNumber = layoutAnchor.pageNum;
      labelAnnotation.Rotation = rotation;

      applyCheckboxOptionLabelFreeTextStyle(labelAnnotation);
      toDraw.push(labelAnnotation);
    }
  }

  if (toDraw.length > 0) {
    await annotationManager.drawAnnotationsFromList(toDraw);
  }
  await syncAnnotations(documentViewer);
}

function addMultiCheckboxOptionRowAtIndex(args: {
  anchorRowTopY: number;
  annotationManager: Core.AnnotationManager;
  fieldManager: Core.Annotations.Forms.FieldManager;
  heightPx: number;
  option: { id: UUID; optionText?: string | null };
  pageNum: number;
  pageX: number;
  rotation: number;
  rowIndex: number;
  selectedAnswerIds: UUID[];
  variableNodeId: UUID;
  widgetsToDraw: Core.Annotations.Annotation[];
}): void {
  const {
    anchorRowTopY,
    annotationManager,
    fieldManager,
    heightPx,
    option,
    pageNum,
    pageX,
    rotation,
    rowIndex,
    selectedAnswerIds,
    variableNodeId,
    widgetsToDraw,
  } = args;
  const optionNodeId = Queries.getCheckboxOptionNodeId(variableNodeId, option.id);
  if (
    getCheckboxWidgetByNodeId(fieldManager, annotationManager, optionNodeId) ||
    (rowIndex === 0 && getCheckboxWidgetByNodeId(fieldManager, annotationManager, variableNodeId))
  ) {
    return;
  }
  const value = selectedAnswerIds.includes(option.id) ? CHECKBOX_CHECKED : PDF_CHECKBOX_UNCHECKED;
  const flags = new pdfTronCore.Annotations.WidgetFlags({});
  flags.set(pdfTronCore.Annotations.WidgetFlags.REQUIRED, false);
  const field = new pdfTronCore.Annotations.Forms.Field(optionNodeId, {
    flags,
    type: PDFFieldType.Btn,
    value,
  });
  const widgetAnnotation = new pdfTronCore.Annotations.CheckButtonWidgetAnnotation(field, {
    appearance: value,
    appearances: { Off: {}, Yes: {} },
    captions: { Normal: '' },
  });
  widgetAnnotation.setCustomData(PDFCustomDataKey.nodeId, optionNodeId);
  widgetAnnotation.PageNumber = pageNum;
  const rowTopY = anchorRowTopY + rowIndex * heightPx;
  const checkboxY = rowTopY + (heightPx - defaultPDFFieldAttributes.fontSizePx) / 2;
  widgetAnnotation.X = pageX;
  widgetAnnotation.Y = checkboxY;
  widgetAnnotation.Width = defaultPDFFieldAttributes.fontSizePx;
  widgetAnnotation.Height = defaultPDFFieldAttributes.fontSizePx;
  widgetAnnotation.rotation = rotation;
  fieldManager.addField(field, true);
  annotationManager.addAnnotation(widgetAnnotation);
  widgetsToDraw.push(widgetAnnotation);

  const labelText = (option.optionText ?? '').trim();
  if (labelText) {
    const createdLabel = createCheckboxOptionLabelForWidget({
      annotationManager,
      labelText,
      optionNodeId,
      pageNum,
      pageX,
      rotation,
      rowTopY,
      widgetAnnotation,
    });
    widgetsToDraw.push(createdLabel);
  }
}

export interface CannedFieldWidgetRef {
  cannedFieldId: string | null | undefined;
  pageNumber?: number | null;
}

/**
 * Select every widget carrying `cannedFieldId`, plus the pages they sit on.
 *
 * One canned field can be placed many times in a document, and each placement
 * is its own PDF form field (named by that placement's `nodeId`). A value write
 * therefore has to reach all of them — matching only the first leaves every
 * later placement rendering blank while its tooltip shows the right value.
 *
 * Apryse returns `''`, not null, from `getCustomData` for an absent key, so an
 * empty target id must select nothing rather than every non-canned widget.
 */
export function selectCannedFieldWidgets<TWidget extends CannedFieldWidgetRef>(
  widgets: readonly TWidget[],
  cannedFieldId: UUID
): { pageNumbers: number[]; widgets: TWidget[] } {
  if (!cannedFieldId) return { pageNumbers: [], widgets: [] };

  const matched = widgets.filter(widget => widget.cannedFieldId === cannedFieldId);
  const pageNumbers = [
    ...new Set(
      matched
        .map(({ pageNumber }) => pageNumber)
        .filter(
          (pageNumber): pageNumber is number =>
            typeof pageNumber === 'number' && Number.isFinite(pageNumber) && pageNumber > 0
        )
    ),
  ].sort((a, b) => a - b);

  return { pageNumbers, widgets: matched };
}

export interface CheckboxPdfOptionRow {
  id: UUID;
  optionText?: string | null;
}

export function computeCheckboxOptionsDiff(
  previousOptions: CheckboxPdfOptionRow[],
  nextOptions: CheckboxPdfOptionRow[]
) {
  const previousOptionIds = previousOptions.map(option => option.id);
  const previousIdSet = new Set(previousOptionIds);
  const nextIdSet = new Set(nextOptions.map(option => option.id));
  const previousTextById = new Map(
    previousOptions.map(option => [option.id, (option.optionText ?? '').trim()] as [UUID, string])
  );

  return {
    addedOptions: nextOptions.filter(option => !previousIdSet.has(option.id)),
    orderChanged:
      previousOptionIds.length === nextOptions.length &&
      previousOptionIds.some((id, index) => id !== nextOptions[index]?.id),
    previousIdSet,
    previousTextById,
    removedIds: previousOptionIds.filter(id => !nextIdSet.has(id)),
  };
}
